import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, CancelTokenSource, Method } from 'axios';
import { stringify } from 'query-string';
import * as React from 'react';

type TableauRestApiConfig<TRequestBody = void, TQueryParams extends Record<string, unknown> = Record<string, unknown>> = {
  readonly restApiVersion: string;
  readonly endpoint: string;
  readonly requestBody?: TRequestBody;
  readonly queryParams?: TQueryParams;
  readonly serverWideRequest?: boolean;
  readonly noPrefix?:boolean;
}

type RenamedAxiosRequestConfig = {
  readonly httpMethod: Method,
  readonly cancelTokenSource?: CancelTokenSource;
}

/**
 * Include some properties on AxiosRequestConfig that were directly defiend on RestApiRequestConfig
 */
type MirroredAxiosRequestConfig<TResult> = Readonly<Partial<Pick<AxiosRequestConfig<TResult>, 'headers' | 'onUploadProgress' | 'onDownloadProgress' | 'cancelToken' | 'signal'>>>;

/**
 * Config for the REST API request; agnostic of the underlying HTTP library
 *
 * I'm leaving the above comment here for historical purposes,
 * but this type was never agnostic beacuse it directly consumed parts of axios 🤷
 * For example, cancelToken, onUploadProgress, and onDownloadProgress were included and canceLToken now deprecated in axios.
 * Since axios was always directly accessible through these config properties, I've done away with this pretense in hopefully a low breaking way.
 * I've included signal which is the current way to abort a request.
 */
export type RestApiRequestConfig<
  TRequestBody = void,
  TResult = Record<string, unknown>,
  TQueryParams extends Record<string, unknown> = Record<string, unknown>
> = TableauRestApiConfig<TRequestBody, TQueryParams> & RenamedAxiosRequestConfig & MirroredAxiosRequestConfig<TResult>

export enum RestErrorSource {
  Server,
  Cancelled,
}

export type RestApiErrorResult = RestApiErrorCancelled | RestApiErrorResponse

export type RestApiErrorCancelled = {
  readonly errorSource: RestErrorSource.Cancelled
}

export type RestApiErrorResponse = {
  readonly errorSource: RestErrorSource.Server;
  readonly data: RestApiErrorData;
  readonly status: number;
  readonly statusText: string;
};

export type RestApiErrorData = {
  readonly error: RestApiError;
};

export type RestApiError = {
  readonly summary: string;
  readonly detail: string;
  readonly code: string;
};

const cancelled: RestApiErrorCancelled = { errorSource: RestErrorSource.Cancelled }

export function getErrorSourceFromRestError(response: RestApiErrorResponse): RestErrorSource {
  return response?.errorSource ?? RestErrorSource.Server;
}

export type JsonRestApiRequestConfig = Omit<RestApiRequestConfig<unknown, Record<string, unknown>>, 'requestBody' | 'headers' | 'queryParams'>;

export type RestApiServiceParams = {
  readonly baseUrl?: string;
  readonly siteLuid: string;
  readonly xsrfToken?: string;
  readonly handle401?: (reason: number) => void;
  readonly wrapRequestWith?: <T, R>(makeRequest: () => Promise<T>) => Promise<R>;
}

const emptyErrorData: RestApiErrorData = {
  error: {
    code: '',
    detail: '',
    summary: '',
  }
}

type RestApiService = {
  readonly makeJsonRestApiRequest: <TRequest, TResult, TQueryParams extends Record<string, unknown> = Record<string, unknown>>(
    config: JsonRestApiRequestConfig,
    request?: TRequest,
    queryParams?: TQueryParams,
  ) => Promise<TResult>;
  readonly makeRestApiRequest: <TRequestBody, TResult, TQueryParams extends Record<string, unknown> = Record<string, unknown>>(
    params: RestApiRequestConfig<TRequestBody, TResult, TQueryParams>
  ) => Promise<TResult>;
}

export function restApiService({ baseUrl, siteLuid, xsrfToken, handle401, wrapRequestWith }: RestApiServiceParams): RestApiService {
  function makeAxiosRequest<TRequestBody, TResult>(config: AxiosRequestConfig<TRequestBody>): Promise<TResult> {
    const axiosConfig: AxiosRequestConfig<TRequestBody> = { ...config, baseURL: baseUrl }

    return axios(axiosConfig)
      .then(
        ({ data }: AxiosResponse<TResult>) => data,
        (response: AxiosError<RestApiErrorData, RestApiErrorData>) => {
          if (axios.isCancel(response)) {
            return Promise.reject(cancelled)
          }

          const innerResponse: RestApiErrorResponse = {
            errorSource: RestErrorSource.Server,
            data: response.response?.data ?? emptyErrorData,
            status: response.response?.status ?? 400,
            statusText: response.response?.statusText ?? 'No status text was provided',
          }

          if (innerResponse.status === 401) {
            const reason = parseInt(innerResponse.data.error.code, 10);
            handle401 && handle401(reason);
          }

          return Promise.reject(innerResponse);
        }
      );
  }

  function makeRestApiRequest<TRequestBody, TResult, TQueryParams extends Record<string, unknown> = Record<string, unknown>>(
    params: RestApiRequestConfig<TRequestBody, TResult, TQueryParams>
  ): Promise<TResult> {
    const { restApiVersion, endpoint, requestBody, queryParams, serverWideRequest, noPrefix, cancelTokenSource, cancelToken, ...config } = params;

    let url = noPrefix? endpoint: serverWideRequest ? `api/${restApiVersion}/${endpoint}` : `api/${restApiVersion}/sites/${siteLuid}/${endpoint}`;

    if (queryParams) {
      url += `?${stringify(queryParams)}`;
    }

    const axiosConfig: AxiosRequestConfig<TRequestBody> = {
      ...config,
      data: requestBody,
      url,
      method: `${config.httpMethod}`,
      cancelToken: cancelTokenSource?.token ?? cancelToken,
    }
    const makeRequest = () => makeAxiosRequest<TRequestBody, TResult>(axiosConfig)
    return wrapRequestWith ? wrapRequestWith(makeRequest) : makeRequest();
  }

  function makeJsonRestApiRequest<TRequest, TResult, TQueryParams extends Record<string, unknown> = Record<string, unknown>>(
    config: JsonRestApiRequestConfig,
    request?: TRequest,
    queryParams?: TQueryParams,
  ): Promise<TResult> {
    const isRequestDefined = (request !== null && request !== undefined);
    const requestString = isRequestDefined ? JSON.stringify(request) : null;

    return makeRestApiRequest({
      ...config,
      queryParams: queryParams,
      requestBody: requestString,
      headers: xsrfToken? {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-XSRF-Token': xsrfToken,
        'WithCredentials': true
      }: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
  }

  return { makeRestApiRequest, makeJsonRestApiRequest }
}

export function useRestApiService(): RestApiService {
  const restApiServiceParams = React.useContext(RestApiServiceParamsContext)
  const [{ makeJsonRestApiRequest, makeRestApiRequest }] = React.useState(restApiService(restApiServiceParams));
  return { makeJsonRestApiRequest, makeRestApiRequest };
}

const defaultRestApiServiceParams: RestApiServiceParams = {
  siteLuid: "",
  baseUrl: window.location.origin,
}

export const RestApiServiceParamsContext = React.createContext<RestApiServiceParams>(defaultRestApiServiceParams);
